import { Description, KeyboardArrowDown, MoreHoriz, Send } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useCallback, useMemo, useRef, useState, type MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { OutlinedIconButton } from '~/components/button';
import { ArbitraryCommandDialog } from '~/components/devices/ArbitraryCommandDialog';
import { useAppContext } from '~/contexts';
import {
  BrightnessDialog,
  PlayOverlayDialog,
  TriggerViewDialog,
  TuneChannelDialog,
  UpdateSoftwareDialog,
  VolumeDialog,
} from '../../components';
import { deviceCapabilitiesFor } from '../../lib/capabilities';
import { PlayContentDialog } from '../../list/components';
import { CommandPopover, type DeviceCommandHandlers } from '../../list/components/CommandPopover';
import type { DeviceLayout__Device as Device } from '../../queries/layout.generated';
export interface ActionsProps {
  device: Device | null | undefined;
  onShowMenu?: MouseEventHandler<HTMLButtonElement>;
}

export const Actions = ({ device, onShowMenu }: ActionsProps) => {
  const { t } = useTranslation();
  const { currentUser, currentNetwork } = useAppContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closePopover = useCallback(() => setPopoverOpen(false), []);

  const [brightnessDialogOpen, setBrightnessDialogOpen] = useState(false);
  const [volumeDialogOpen, setVolumeDialogOpen] = useState(false);
  const [tuneChannelDialogOpen, setTuneChannelDialogOpen] = useState(false);
  const [playContentDialogOpen, setPlayContentDialogOpen] = useState(false);
  const [playOverlayDialogOpen, setPlayOverlayDialogOpen] = useState(false);
  const [triggerViewDialogOpen, setTriggerViewDialogOpen] = useState(false);
  const [updateSoftwareDialogOpen, setUpdateSoftwareDialogOpen] = useState(false);
  const [arbitraryDialogOpen, setArbitraryDialogOpen] = useState(false);

  const selected = useMemo(() => ({ ids: device ? [device.id] : [] }), [device]);

  const popoverHandlers = useMemo<DeviceCommandHandlers>(
    () => ({
      onBrightness: () => setBrightnessDialogOpen(true),
      onPlayContent: () => setPlayContentDialogOpen(true),
      onPlayOverlay: () => setPlayOverlayDialogOpen(true),
      onSendArbitrary: () => setArbitraryDialogOpen(true),
      onTriggerView: () => setTriggerViewDialogOpen(true),
      onTuneChannel: () => setTuneChannelDialogOpen(true),
      onUpdateSoftware: () => setUpdateSoftwareDialogOpen(true),
      onVolume: () => setVolumeDialogOpen(true),
    }),
    [],
  );

  const disabledItems = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    if (!device) return result;
    const caps = deviceCapabilitiesFor(device);
    const notSupported = t('pages.devices.bulk_actions.disabled_reasons.not_supported');
    if (!caps.brightness) result.set_brightness = notSupported;
    if (!caps.absoluteVolume) result.set_volume = notSupported;
    if (!caps.mute) {
      result.mute = notSupported;
      result.unmute = notSupported;
    }
    if (!caps.analyzeChannels) result.analyze_channels = notSupported;
    if (!caps.physicalKeys) {
      result.keys_lock = notSupported;
      result.keys_unlock = notSupported;
    }
    if (!caps.remote) {
      result.remote_lock = notSupported;
      result.remote_unlock = notSupported;
    }
    if (!device.activeShow) {
      result.play_view = t('pages.devices.bulk_actions.disabled_reasons.no_active_show');
    } else if (device.activeShow.show.viewCount < 2) {
      result.play_view = t('pages.devices.bulk_actions.disabled_reasons.single_view_show');
    }
    if (!device.activeShow?.show.channelGuide?.channelCount) {
      result.tune_channel = t('pages.devices.bulk_actions.disabled_reasons.no_channel_guide');
    }
    if (!device.realtime) {
      const reason = t('pages.devices.bulk_actions.disabled_reasons.not_realtime');
      result.play_overlay = reason;
      result.stop_overlay = reason;
    }
    if (!device.canUpdateSoftware.value) {
      result.update_software = t('pages.devices.bulk_actions.disabled_reasons.software_up_to_date');
    }
    return result;
  }, [device, t]);

  if (!device || !device.canUpdate.value) return <Box />;

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {currentUser.admin && (
        <Tooltip title={t('pages.devices.show.actions.download_logs')}>
          <Button
            variant="outlined"
            disabled={!device.logUri}
            href={device.logUri ?? ''}
            startIcon={<Description color="inherit" />}
          >
            {t('pages.devices.show.actions.download_logs')}
          </Button>
        </Tooltip>
      )}

      <Button
        ref={buttonRef}
        aria-controls={popoverOpen ? 'send-actions-menu' : undefined}
        aria-expanded={popoverOpen ? 'true' : undefined}
        aria-haspopup="true"
        startIcon={<Send />}
        endIcon={<KeyboardArrowDown />}
        id="send-actions-button"
        onClick={() => setPopoverOpen(true)}
        variant="outlined"
      >
        {t('pages.devices.show.actions.send_command')}
      </Button>

      <CommandPopover
        {...popoverHandlers}
        anchorEl={buttonRef.current}
        disabledItems={disabledItems}
        labelledBy="send-actions-button"
        menuId="send-actions-menu"
        onClose={closePopover}
        open={popoverOpen}
        selected={selected}
        subject={`${device.name} (${device.id})`}
      />

      {(currentUser.admin || currentNetwork.canManage.value) && (
        <OutlinedIconButton
          label={t('common.buttons.more_actions')}
          onClick={onShowMenu}
          size="large"
        >
          <MoreHoriz />
        </OutlinedIconButton>
      )}

      {brightnessDialogOpen && (
        <BrightnessDialog
          onClose={() => setBrightnessDialogOpen(false)}
          onComplete={() => setBrightnessDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {volumeDialogOpen && (
        <VolumeDialog
          onClose={() => setVolumeDialogOpen(false)}
          onComplete={() => setVolumeDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {tuneChannelDialogOpen && (
        <TuneChannelDialog
          channelGuideId={device.activeShow?.show.channelGuide?.id}
          onClose={() => setTuneChannelDialogOpen(false)}
          onComplete={() => setTuneChannelDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {playContentDialogOpen && (
        <PlayContentDialog
          onClose={() => setPlayContentDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {playOverlayDialogOpen && (
        <PlayOverlayDialog
          onClose={() => setPlayOverlayDialogOpen(false)}
          onComplete={() => setPlayOverlayDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {triggerViewDialogOpen && device.activeShow && (
        <TriggerViewDialog
          activeShowId={device.activeShow.show.id}
          currentViewId={device.currentView?.id}
          onClose={() => setTriggerViewDialogOpen(false)}
          selected={selected}
        />
      )}
      {updateSoftwareDialogOpen && (
        <UpdateSoftwareDialog
          onClose={() => setUpdateSoftwareDialogOpen(false)}
          onComplete={() => setUpdateSoftwareDialogOpen(false)}
          selected={selected}
        />
      )}
      {arbitraryDialogOpen && (
        <ArbitraryCommandDialog
          onClose={() => setArbitraryDialogOpen(false)}
          onComplete={() => setArbitraryDialogOpen(false)}
          open
          selected={selected}
        />
      )}
    </Box>
  );
};
